import React, { useState, useRef } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Camera, Trash2, Upload, Link as LinkIcon } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function ProfilePage() {
  const { API, user, setUser } = useAuth();
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use JPG, PNG, WEBP ou GIF');
      return;
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 5MB');
      return;
    }

    setSelectedFile(file);
    
    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      toast.error('Selecione um arquivo');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/auth/profile/photo/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true
      });

      setUser(response.data.user);
      toast.success('Foto de perfil atualizada!');
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao fazer upload da foto';
      toast.error(message);
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleUpdatePhoto = async () => {
    if (!photoUrl.trim()) {
      toast.error('Digite a URL da foto');
      return;
    }

    // Validar se é uma URL válida
    try {
      new URL(photoUrl);
    } catch {
      toast.error('URL inválida');
      return;
    }

    setUploading(true);
    try {
      const response = await axios.put(`${API}/auth/profile/photo`, {
        photo_url: photoUrl
      }, { withCredentials: true });

      setUser(response.data.user);
      toast.success('Foto de perfil atualizada!');
      setPhotoUrl('');
    } catch (error) {
      toast.error('Erro ao atualizar foto');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setUploading(true);
    try {
      await axios.delete(`${API}/auth/profile/photo`, { withCredentials: true });
      setUser({ ...user, picture: null });
      toast.success('Foto removida com sucesso');
    } catch (error) {
      toast.error('Erro ao remover foto');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Meu Perfil</h1>
        <p className="text-gray-500">Gerencie suas informações pessoais</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Card da Foto de Perfil */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Foto de Perfil
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Preview da Foto */}
            <div className="flex justify-center">
              <div className="relative">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-32 h-32 rounded-full object-cover border-4 border-blue-500"
                  />
                ) : user?.picture ? (
                  <img
                    src={user.picture}
                    alt="Foto de perfil"
                    className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                    onError={(e) => {
                      e.target.src = '';
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-4xl font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            </div>

            {/* Formulário de Upload com Tabs */}
            <Tabs defaultValue="upload" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="upload">
                  <Upload className="w-4 h-4 mr-2" />
                  Enviar Arquivo
                </TabsTrigger>
                <TabsTrigger value="url">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Usar URL
                </TabsTrigger>
              </TabsList>

              {/* Tab: Upload de Arquivo */}
              <TabsContent value="upload" className="space-y-3 mt-4">
                <div>
                  <Label htmlFor="fileInput" className="text-sm">Selecionar Arquivo</Label>
                  <Input
                    ref={fileInputRef}
                    id="fileInput"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={handleFileSelect}
                    disabled={uploading}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    JPG, PNG, WEBP ou GIF (máx. 5MB)
                  </p>
                </div>

                {selectedFile && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800 font-medium">
                      📁 {selectedFile.name}
                    </p>
                    <p className="text-xs text-blue-600">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleUploadFile}
                  disabled={uploading || !selectedFile}
                  className="w-full"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Enviando...' : 'Enviar Foto'}
                </Button>
              </TabsContent>

              {/* Tab: URL */}
              <TabsContent value="url" className="space-y-3 mt-4">
                <div>
                  <Label htmlFor="photoUrl" className="text-sm">URL da Foto</Label>
                  <Input
                    id="photoUrl"
                    type="url"
                    placeholder="https://exemplo.com/foto.jpg"
                    value={photoUrl}
                    onChange={(e) => setPhotoUrl(e.target.value)}
                    disabled={uploading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cole o link de uma imagem hospedada online
                  </p>
                </div>

                <Button
                  onClick={handleUpdatePhoto}
                  disabled={uploading || !photoUrl.trim()}
                  className="w-full"
                >
                  <LinkIcon className="w-4 h-4 mr-2" />
                  {uploading ? 'Atualizando...' : 'Usar URL'}
                </Button>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800 font-medium mb-1">💡 Dicas:</p>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• Use serviços como Imgur ou Google Drive</li>
                    <li>• Link deve terminar em .jpg, .png ou .webp</li>
                  </ul>
                </div>
              </TabsContent>
            </Tabs>

            {/* Botão Remover */}
            {user?.picture && (
              <Button
                variant="outline"
                onClick={handleRemovePhoto}
                disabled={uploading}
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remover Foto
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Card de Informações do Usuário */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Informações da Conta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-500">Nome</Label>
                <p className="text-lg font-medium">{user?.name}</p>
              </div>

              <div>
                <Label className="text-sm text-gray-500">E-mail</Label>
                <p className="text-lg font-medium">{user?.email}</p>
              </div>

              <div>
                <Label className="text-sm text-gray-500">Função</Label>
                <p className="text-lg font-medium capitalize">
                  {user?.role === 'admin' ? (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
                      Administrador
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                      {user?.role}
                    </span>
                  )}
                </p>
              </div>

              <div>
                <Label className="text-sm text-gray-500">Data de Criação</Label>
                <p className="text-lg font-medium">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '-'}
                </p>
              </div>
            </div>

            {/* Estatísticas de Permissões */}
            {user?.permissions && (
              <div className="mt-6 pt-6 border-t">
                <Label className="text-sm text-gray-500 mb-3 block">Módulos com Acesso</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(user.permissions).map(([module, perms]) => (
                    perms.view && (
                      <div key={module} className="flex items-center gap-2 text-sm">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="capitalize">{module}</span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
